'use client';

import { Fragment, useState } from 'react';
import { Listing } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';

import Button from '@/components/ui/button';

const PropertyDescription: React.FC<{ listing: Listing }> = ({ listing }) => {
  const [showMore, setShowMore] = useState(false);
  if (!listing.marketing?.desc_primary) return null;
  const desc_facility =
    listing.marketing?.desc_facility ||
    listing?.resort?.marketing?.desc_facility;
  const desc_location =
    listing.marketing?.desc_location ||
    listing?.resort?.marketing?.desc_location;
  const desc_transport =
    listing.marketing?.desc_transport ||
    listing.resort?.marketing?.desc_transport;
  const hasPostDisclaimer =
    listing.marketing?.disclaimer_post_one ||
    listing.resort?.marketing?.disclaimer_post_one ||
    listing.marketing?.disclaimer_post_two ||
    listing.resort?.marketing?.disclaimer_post_two;
  const hasPreDisclaimer =
    listing.marketing?.disclaimer_pre ||
    listing.resort?.marketing?.disclaimer_pre;
  const listingPreDesc = listing.resort?.marketing?.desc_pre_listing;
  const listingPostDesc = listing.resort?.marketing?.desc_post_listing;
  return (
    <Fragment>
      {hasPreDisclaimer && (
        <div className="disclaimers">
          {listing.marketing?.disclaimer_pre && (
            <div className="disclaimer lg:my-[1rem]">
              <div
                className="ut-md flex w-full"
                dangerouslySetInnerHTML={{
                  __html: listing.marketing.disclaimer_pre.replaceAll(
                    '\n',
                    '<br />'
                  ),
                }}
              ></div>
            </div>
          )}
          {listing.resort?.marketing?.disclaimer_pre && (
            <div className="disclaimer lg:my-[1rem]">
              <div
                className="ut-md flex w-full"
                dangerouslySetInnerHTML={{
                  __html: listing.resort.marketing.disclaimer_pre.replaceAll(
                    '\n',
                    '<br />'
                  ),
                }}
              ></div>
            </div>
          )}
        </div>
      )}
      {listing.marketing?.desc_primary && (
        <div className="flex flex-col gap-[0.5rem]">
          <span className="caps-lg text-primary-main">The Property</span>
          <p
            id="marketing-desc"
            className={showMore ? 'description-open' : 'description-clamped'}
            dangerouslySetInnerHTML={{
              __html:
                `${listingPreDesc ? listingPreDesc + '<br /><br />' : ''}` +
                listing.marketing.desc_primary.replaceAll('\n', '<br />') +
                `${listingPostDesc ? '<br /><br />' + listingPostDesc : ''}`,
            }}
          ></p>
        </div>
      )}
      {showMore && desc_facility && (
        <div className="flex flex-col gap-[0.5rem]">
          <span className="caps-lg text-primary-main">Facilities</span>
          <p
            id="marketing-desc"
            className="description-open"
            dangerouslySetInnerHTML={{
              __html: desc_facility.replaceAll('\n', '<br />'),
            }}
          ></p>
        </div>
      )}
      {showMore && desc_location && (
        <div className="flex flex-col gap-[0.5rem]">
          <span className="caps-lg text-primary-main">Location</span>
          <p
            id="marketing-desc"
            className="description-open"
            dangerouslySetInnerHTML={{
              __html: desc_location.replaceAll('\n', '<br />'),
            }}
          ></p>
        </div>
      )}
      {showMore && desc_transport && (
        <div className="flex flex-col gap-[0.5rem]">
          <span className="caps-lg text-primary-main">Getting There?</span>
          <p
            id="marketing-desc"
            className="description-open"
            dangerouslySetInnerHTML={{
              __html: desc_transport.replaceAll('\n', '<br />'),
            }}
          ></p>
        </div>
      )}
      {showMore && hasPostDisclaimer && (
        <div className="disclaimers">
          {listing.marketing?.disclaimer_post_one && (
            <div className="disclaimer lg:my-[1rem]">
              <div
                className="ut-md flex w-full"
                dangerouslySetInnerHTML={{
                  __html: listing.marketing.disclaimer_post_one
                    .replaceAll('\n', '<br />')
                    .replaceAll('***', ''),
                }}
              ></div>
            </div>
          )}
          {listing.resort?.marketing?.disclaimer_post_one && (
            <div className="disclaimer lg:my-[1rem]">
              <div
                className="ut-md flex w-full"
                dangerouslySetInnerHTML={{
                  __html: listing.resort.marketing.disclaimer_post_one
                    .replaceAll('\n', '<br />')
                    .replaceAll('***', ''),
                }}
              ></div>
            </div>
          )}
          {listing.marketing?.disclaimer_post_two && (
            <div className="disclaimer lg:my-[1rem]">
              <div
                className="ut-md flex w-full"
                dangerouslySetInnerHTML={{
                  __html: listing.marketing.disclaimer_post_two
                    .replaceAll('\n', '<br />')
                    .replaceAll('***', ''),
                }}
              ></div>
            </div>
          )}
          {listing.resort?.marketing?.disclaimer_post_two && (
            <div className="disclaimer lg:my-[1rem]">
              <div
                className="ut-md flex w-full"
                dangerouslySetInnerHTML={{
                  __html: listing.resort.marketing.disclaimer_post_two
                    .replaceAll('\n', '<br />')
                    .replaceAll('***', ''),
                }}
              ></div>
            </div>
          )}
        </div>
      )}
      {showMore && listing.tax_permit_id && (
        <div className="flex flex-col text-black-1">
          <p className="font-bold text-black-1">Tax Id</p>
          {listing.tax_permit_id}
        </div>
      )}

      <footer className="pb-[1rem]">
        <Button
          onClick={() => {
            setShowMore((prev) => !prev);
          }}
          style="text"
          className="!text-secondary-main transition-all duration-200 hover:!text-secondary-light"
        >
          <div className="flex flex-row items-center">
            {showMore ? 'Hide' : 'Read More'}
            <svg
              className={clsx(
                '-mt-[0.25rem] ml-[0.5rem]',
                showMore && 'rotate-180'
              )}
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="12"
              viewBox="0 0 18 12"
              fill="none"
            >
              <path
                d="M2 2.5L9 9.5L16 2.5"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </Button>
      </footer>
    </Fragment>
  );
};

export default PropertyDescription;
